/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
// Costume-only checkout: no ticket order here, so the existing createCheckoutFAC is used.
import { CHECK_PAYMENT_FROM_FAC } from "../graphql/checkoutFAC.mutation";
import { CostumePageService } from "../../costumePageService";
import { VApplication } from "../../../application";
import { LoggerModule } from "@vlinder-web/logger-module-react";

const is = require("is_js");
const log: any = LoggerModule?.getInstance();
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface CreatePayFACProps {
  amount: number;
  currency?: string;
  notes?: string;
  payOrder?: boolean;
  mode?: string;
}
export interface ICreatePayFACMethods extends IPluginMethodMap {
  createPayFAC(args: any): Promise<any>;
}

export class CreatePayFACService implements IAgentPlugin {
  readonly methods: ICreatePayFACMethods;

  constructor() {
    this.methods = {
      createPayFAC: this.createPayFAC.bind(this),
    };
  }

  async createPayFAC(args: any) {
    const graphqlModule = getGraphQLModule();
    console.log("args44print", args);
    const client = graphqlModule.getClient("graphql-gateway");
    if (args?.event?.payOrder === null || args?.event?.payOrder === undefined) {
      throw Error("Pay order cannot be null or empty");
    }

    const allocations = (args?.event?.allocations || []).map((alloc: any) => ({
      costumeValueId: alloc.costumeValueId,
      amount: parseFloat(alloc.amount),
    }));

    let variables: any = {
      data: {
        cardDetail: {
          cardExp: args?.event?.expiry,
          cardHolderName: args?.event?.name,
          cardPan: (args?.event?.number || "").replace(/\s+/g, ""),
        },
        currency: args?.event?.currency || "TTD",
        totalAmount: parseFloat(args?.event?.amount),
        payOrder: args?.event?.payOrder,
        platformFee: args?.event?.platformFee
          ? parseFloat(args?.event?.platformFee)
          : 0,
        allocations: allocations,
        email: args?.event?.myEmail || args?.event?.email || "",
        phoneNumber: args?.event?.phone || "",
      },
    };
    if (args?.event?.code) {
      variables.data.discountCode = args?.event?.code;
    }
    console.log(variables);

    // Held tickets on this Box (#736): one FAC charge for the costume share and the
    // tickets (payCostumeAndTickets), or tickets only (payForTickets). These save
    // the ticket txId for the payment result page and handle the 3-D Secure redirect.
    const ticketOrderId = args?.event?.ticketOrderId;
    if (ticketOrderId) {
      const costumeSrv = CostumePageService.getInstance();
      const costumeAmount = parseFloat(args?.event?.amount) || 0;
      const result: any =
        costumeAmount > 0
          ? await costumeSrv.payCostumeAndTickets({
              ticketOrderId,
              payOrder: variables.data.payOrder,
              totalAmount: costumeAmount,
              platformFee: variables.data.platformFee,
              currency: variables.data.currency,
              allocations,
              discountCode: args?.event?.code || null,
              cardDetail: variables.data.cardDetail,
              email: variables.data.email,
              phoneNumber: variables.data.phoneNumber,
            })
          : await costumeSrv.payForTickets({
              ticketOrderId,
              cardDetail: variables.data.cardDetail,
              email: variables.data.email,
              phoneNumber: variables.data.phoneNumber,
            });
      return {
        ...result,
        RedirectData: result?.RedirectData,
        createCheckoutFAC: { RedirectData: result?.RedirectData },
      };
    }

    try {
      if (client) {
        const res = await client.mutate({
          mutation: CHECK_PAYMENT_FROM_FAC,
          variables: variables,
          fetchPolicy: "no-cache",
        });
        const payResult = { session: res?.data?.createCheckoutFAC };
        let redirectData = payResult?.session;
        if (typeof redirectData === "string") {
          try {
            const parsed = JSON.parse(redirectData);
            if (parsed?.RedirectData) {
              redirectData = parsed.RedirectData;
            }
          } catch (e) {}
        } else if (redirectData?.RedirectData) {
          redirectData = redirectData.RedirectData;
        }

        if (is.existy(redirectData)) {
          const redirectStr = String(redirectData);
          if (
            redirectStr.startsWith("http://") ||
            redirectStr.startsWith("https://")
          ) {
            window.location.href = redirectStr;
          } else {
            document.write(redirectStr);
          }
        }
        return {
          ...payResult,
          RedirectData: redirectData,
          createCheckoutFAC: {
            RedirectData: redirectData,
          },
        };
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
